import { VcfDomain } from 'pcs-domain';
import {
  DomainComponent,
  DomainComponentPart,
  QuickSizerApiComponentSizing,
} from '../../feature-modules/quick-sizer/models/quick-sizer.models';
import { PredicateHelper } from '../utils/predicate-helper';

export interface DataproviderSizingTier {
  id?: string;
  vcpu?: { value?: string | number };
  memory?: { value?: string | number };
  storage?: { value?: string | number };
  numberOfVMs?: number;
}

/** A dataprovider `/components` row. `defaultState` is not read: default selection follows the forward sizer. */
export interface DataproviderComponentRow {
  id?: number;
  name?: string;
  businessName?: string;
  minNumberOfVms?: number;
  defaultSizing?: string;
  sizing?: DataproviderSizingTier[];
}

/** Reachable only through a composite card. Same list as `SoftwareComponentsComponent.getComponents`. */
export const COMPOSITE_ONLY_COMPONENTS: readonly string[] = [
  'VCF_OPERATIONS',
  'FLEET_MANAGEMENT',
  'VCF_OPERATIONS_COLLECTOR',
  'VRNI',
  'VRNI_COLLECTOR',
];

/**
 * Starting sizes for the components the forward sizer sizes from the VM count. Its engine suggests SMALL
 * for small workloads (components.yml); the Quick Sizer engine grows them and the result replaces these.
 */
export const STARTING_BRICK_SIZES: Readonly<Record<string, string>> = {
  VCF_AUTOMATION: 'SMALL',
  VRNI: 'SMALL',
  VRNI_COLLECTOR: 'SMALL',
};

/** Only these input types map to a component. */
const COMPONENT_INPUT_TYPES = ['COMPONENT_DROPDOWN', 'MULTI_COMPONENT_DROPDOWN'];

function num(value: string | number | undefined): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Explicit default, else the option flagged `isDefault`, else the first option (as in the forward sizer). */
function resolvedDefault(input: VcfDomain.SubGroupInput): string | undefined {
  if (input.default) {
    return input.default;
  }
  const option = input.options?.find(o => o.isDefault) ?? input.options?.[0];
  return option?.id;
}

/** `PredicateHelper` context for one card: its flags plus its resolved input defaults. */
function predicateContext(subGroup: VcfDomain.SubGroup): Record<string, unknown> {
  const context: Record<string, unknown> = {
    productName: subGroup.subGroupId,
    productSelected: subGroup.isEnabled === true,
    isRequired: subGroup.isRequired,
  };
  for (const input of subGroup.inputs ?? []) {
    const value = resolvedDefault(input);
    if (value !== undefined) {
      context[input.id] = value;
    }
  }
  return context;
}

/** An empty predicate means "always show". `evaluatePredicate` throws on one. */
function predicatePasses(
  predicate: VcfDomain.Predicate | undefined,
  context: Record<string, unknown>[],
): boolean {
  if (!predicate?.conditions?.length) {
    return true;
  }
  return PredicateHelper.evaluatePredicate(predicate, context);
}

/** The starting size, when the catalog has that tier; otherwise the default applies, as in the engine. */
function startingSize(productName: string, row: DataproviderComponentRow | undefined): string | undefined {
  const start = STARTING_BRICK_SIZES[productName.toUpperCase()];
  return start && row?.sizing?.some(t => (t.id ?? '').toUpperCase() === start) ? start : undefined;
}

function buildPart(
  productName: string,
  input: VcfDomain.SubGroupInput,
  rowsByName: Map<string, DataproviderComponentRow>,
): DomainComponentPart {
  const row = rowsByName.get(productName.toUpperCase());
  const brickSize = startingSize(productName, row)
    ?? input.default?.toUpperCase()
    ?? row?.defaultSizing?.toUpperCase()
    ?? 'FIXED';
  const tiers = row?.sizing ?? [];
  const tier = tiers.find(t => (t.id ?? '').toUpperCase() === brickSize) ?? tiers[0];
  const vms = tier?.numberOfVMs ?? row?.minNumberOfVms ?? input.minNumberOfVms ?? 0;
  return {
    productName,
    label: row?.businessName ?? productName,
    brickSize,
    vms,
    vcpu: vms * num(tier?.vcpu?.value),
    memoryGB: vms * num(tier?.memory?.value),
    storageGB: vms * num(tier?.storage?.value),
    missingSizing: !row || !tier,
  };
}

/** Turns a forward-sizer component group, already passed through `getWldComponentsInputConfig`, into cards. */
export function buildQuickSizerCards(
  group: VcfDomain.Group | undefined,
  rows: DataproviderComponentRow[] | null | undefined,
): DomainComponent[] {
  const rowsByName = new Map((rows ?? []).map(row => [(row.name ?? '').toUpperCase(), row]));
  const subGroups = group?.subGroups ?? [];
  const groupContext = subGroups.map(predicateContext);
  const cards: DomainComponent[] = [];

  for (const subGroup of subGroups) {
    if (COMPOSITE_ONLY_COMPONENTS.includes(subGroup.subGroupId)) {
      continue;
    }
    // Required but disabled means the backend decides visibility (e.g. AVI_CONTROLLER), so hide it.
    if (subGroup.isEnabled === false && subGroup.isRequired === true) {
      continue;
    }
    if (!predicatePasses(subGroup.predicate, groupContext)) {
      continue;
    }

    const context = [predicateContext(subGroup)];
    const parts: DomainComponentPart[] = [];
    for (const input of subGroup.inputs ?? []) {
      if (!COMPONENT_INPUT_TYPES.includes(input.type)) {
        continue;
      }
      if (!predicatePasses(input.predicate, context)) {
        continue;
      }
      parts.push(buildPart(input.componentId || subGroup.subGroupId, input, rowsByName));
    }

    if (parts.length === 0) {
      continue;
    }

    cards.push({
      cardId: subGroup.subGroupId,
      title: subGroup.subGroupName,
      isRequired: subGroup.isRequired,
      enabledByDefault: subGroup.isEnabled === true,
      ...rollUp(parts),
    });
  }

  return cards;
}

/** Card size and totals rolled up over its parts. The card `brickSize` is the first part's. */
export function rollUp(
  parts: DomainComponentPart[],
): Pick<DomainComponent, 'parts' | 'brickSize' | 'vms' | 'vcpu' | 'memoryGB' | 'storageGB'> {
  return {
    parts,
    brickSize: parts[0]?.brickSize ?? 'FIXED',
    vms: parts.reduce((total, p) => total + p.vms, 0),
    vcpu: parts.reduce((total, p) => total + p.vcpu, 0),
    memoryGB: parts.reduce((total, p) => total + p.memoryGB, 0),
    storageGB: parts.reduce((total, p) => total + p.storageGB, 0),
  };
}

/**
 * Applies the sizes the engine charged to each management part, setting `defaultBrickSize` where
 * a size grew. Parts not in the response keep their figures. Returns new objects.
 */
export function applyComponentSizing<T extends DomainComponent>(
  cards: T[],
  sizing: QuickSizerApiComponentSizing[],
): T[] {
  const byName = new Map(
    sizing
      .filter(s => s.domain === 'MANAGEMENT' && s.brickSize)
      .map(s => [s.name.toUpperCase(), s]),
  );
  if (byName.size === 0) {
    return cards;
  }
  return cards.map(card => {
    const parts = card.parts.map(part => {
      const charged = byName.get(part.productName.toUpperCase());
      if (!charged?.brickSize) {
        return part;
      }
      const next: DomainComponentPart = {
        ...part,
        brickSize: charged.brickSize,
        vms: charged.vms,
        vcpu: charged.vcpu,
        memoryGB: charged.memoryGb,
        storageGB: charged.storageGb,
      };
      if (charged.defaultBrickSize && charged.brickSize !== charged.defaultBrickSize) {
        next.defaultBrickSize = charged.defaultBrickSize;
      } else {
        delete next.defaultBrickSize;
      }
      return next;
    });
    return { ...card, ...rollUp(parts) };
  });
}
